import { message as Message } from "ant-design-vue";
import { AxiosError } from "axios";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { showToast } from "vant";
import { ACCESS_SESSION, ACCESS_TOKEN, CURRENT_CORP_ID } from "~/enum/cache/cookie";
import { API_URL } from "~/env";
import { useMainStore } from "~/stores";
import { errorHandler } from "~/utils/axios-error";
import Common from "~/utils/common";
import Cookie from "~/utils/cookie";
import DeviceUtils from "~/utils/device-utils";
import { useRefreshToken } from "~/utils/refresh-token";
import { fetchEventSource } from "@microsoft/fetch-event-source";

const { refreshToken } = useRefreshToken();

getSessionId(); //初始化sessionid
function getSessionId() {
  if (process.server) return "";
  let id = Cookie.get(ACCESS_SESSION);
  if (!id) {
    id = Common.getUUID();
    Cookie.set(ACCESS_SESSION, id);
  }
  return id;
}

/**
 * 获取页面唯一标识
 */
function getIdentify() {
  if (process.server) return Common.getUUID();
  let id = sessionStorage.getItem("page_identify") as string;
  if (id) return id;
  id = Common.getUUID(); //初始化identify
  sessionStorage.setItem("page_identify", id);
  return id;
}

function getVersionType() {
  return (useMainStore() as any).versionType.toString();
}

export function axiosConfig(
  $axios: AxiosInstance,
  options?: {
    onRequest?: (config: AxiosRequestConfig) => void;
    onResponse?: (response: AxiosResponse) => void;
    onError?: (error: AxiosError) => void;
  },
) {
  // 设置请求拦截
  $axios.interceptors.request.use((config) => {
    // 确保 headers 存在
    if (!config.headers) {
      config.headers = {} as any; // 使用类型断言来避免类型错误
    }

    // 设置通用头部
    // 如果是 FormData，不设置 Content-Type，让浏览器自动设置 multipart/form-data
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }
    config.headers["Access-Identify"] = getIdentify();
    config.headers.platform = "portal";
    config.headers.versionType = getVersionType();
    const curentcorpid = Cookie.get(CURRENT_CORP_ID);
    if (curentcorpid) {
      config.headers.currentcorpid = curentcorpid;
    }
    //只有客户端调用才传token
    if (process.client) {
      const SESSION_ID = getSessionId();
      config.headers["Access-Session"] = SESSION_ID;
      const token = Cookie.get(ACCESS_TOKEN);
      if (token) {
        config.headers.Authorization = token;
      }
    } else {
      try {
        // let url = "";
        // if (config.url) {
        //   url = config.url.includes("http") ? config.url : (config.baseURL || "") + config.url;
        // }
        // const method = config.method?.toUpperCase();
        // console.log(`Server Request URL：[${method}] ${url}`);
        if (config.headers?.common?.cookie) {
          const SESSION_ID = Cookie.get(ACCESS_SESSION, config.headers.common.cookie);
          const token = Cookie.get(ACCESS_TOKEN, config.headers.common.cookie);
          if (token) config.headers.Authorization = token;
          if (SESSION_ID) config.headers["Access-Session"] = SESSION_ID;
        }
      } catch (e) {
        console.warn(`server request error:${e}`);
      }
    }

    if (options?.onRequest) options.onRequest(config);

    return config;
  });

  // 设置响应拦截
  $axios.interceptors.response.use(
    (response) => {
      const status = response.status;

      if (options?.onResponse) options.onResponse(response);

      if (status === 200) {
        // 返回接口数据
        return response.data;
      } else {
        // 如果请求失败的，打印出相应的错误信息，更好的修改。
        const responseConfig = response.config || {};
        console.error("响应拦截报错提示： ", {
          url: (responseConfig.baseURL || "") + (responseConfig.url || ""),
          status: response.status,
          statusText: response.statusText,
          method: responseConfig.method,
          headers: responseConfig.headers,
          data: responseConfig.data,
          params: responseConfig.params,
          responseData: response.data,
        });
        return response;
      }
    },
    (error) => {
      if (options?.onError) options.onError(error);

      return errorHandler(error, $axios);
    },
  );

  // 最后返回$axios对象
  return $axios;
}

// 请求队列，用于跟踪正在进行的请求
const requestQueue: {
  config: AxiosRequestConfig;
  response: Promise<RequestResponse>;
}[] = [];

/**
 * 发送请求，不处理异常（同时发送相同请求只会触发一次真实请求，其他请求会等待已经发送的请求一起返回结果）
 */
export function sendAxios($axios: AxiosInstance, config: AxiosRequestConfig) {
  const existingRequest = requestQueue.find(({ config: request }) => {
    return (
      request.url === config.url &&
      request.method === config.method &&
      JSON.stringify(request.data || request.params) === JSON.stringify(config.data || config.params)
    );
  });
  if (existingRequest) {
    return existingRequest.response;
  }
  const response = $axios.request<any, RequestResponse>(config);
  response.finally(() => {
    const index = requestQueue.findIndex(({ config: request }) => {
      return (
        request.url === config.url &&
        request.method === config.method &&
        JSON.stringify(request.data || request.params) === JSON.stringify(config.data || config.params)
      );
    });
    if (index !== -1) {
      requestQueue.splice(index, 1);
    }
  });
  requestQueue.push({ config, response });
  return response;
}

/**
 * 发送请求，处理异常（同时发送相同请求只会触发一次真实请求，其他请求会等待已经发送的请求一起返回结果）
 */
export function sendRequest<T = any>($axios: AxiosInstance, config: AxiosRequestConfig) {
  return interceptor<T>(sendAxios($axios, config));
}

/**
 * 发送SSE请求
 */
export function sendSSERequest(
  url: string,
  method: Method,
  data?: any,
  config: FetchEventSourceConfig = {},
  ext: boolean = false,
) {
  config.method = method.toUpperCase();
  config.headers = config.headers || {};
  config.headers.platform = "portal";
  config.headers["Content-Type"] = "application/json";
  config.headers["Access-Identify"] = getIdentify();
  config.headers.versionType = getVersionType();
  if (data) {
    if (config.method === "GET") {
      url += `?${Object.keys(data)
        .map((key) => `${key}=${data[key]}`)
        .join("&")}`;
    } else {
      config.body = JSON.stringify(data);
    }
  }
  if (process.client) {
    const SESSION_ID = getSessionId();
    config.headers["Access-Session"] = SESSION_ID;
    const token = Cookie.get(ACCESS_TOKEN);
    if (token && !config.headers.Authorization) config.headers.Authorization = token;
  } else {
    try {
      if (config.headers?.cookie) {
        const SESSION_ID = Cookie.get(ACCESS_SESSION, config.headers.cookie);
        const token = Cookie.get(ACCESS_TOKEN, config.headers.cookie);
        if (token && !config.headers.Authorization) config.headers.Authorization = token;
        if (SESSION_ID) config.headers["Access-Session"] = SESSION_ID;
      }
    } catch (e) {
      console.warn(`server request error:${e}`);
    }
  }
  const apiUrl =
    ext ? url
    : process.env.NODE_ENV === "development" ? API_URL + url
    : location.origin + url;
  return fetchEventSource(
    apiUrl,
    Object.assign({}, config, {
      onopen: async (response: Response) => {
        if (response.status === 403) {
          await refreshToken();
        }
        config.onopen?.(response);
      },
    }),
  );
}

/**
 * 下载文件
 * @param url
 * @param params
 */
export function downloadPOST(
  url: string,
  params?: Record<string, any>,
  outName?: string,
  headers: Record<string, string> = {},
) {
  if (process.client) {
    const SESSION_ID = getSessionId();
    headers["Access-Session"] = SESSION_ID;
    const token = Cookie.get(ACCESS_TOKEN);
    if (token && !headers.Authorization) headers.Authorization = token;
  } else {
    try {
      if (headers?.cookie) {
        const SESSION_ID = Cookie.get(ACCESS_SESSION, headers.cookie);
        const token = Cookie.get(ACCESS_TOKEN, headers.cookie);
        if (token && !headers.Authorization) headers.Authorization = token;
        if (SESSION_ID) headers["Access-Session"] = SESSION_ID;
      }
    } catch (e) {
      console.warn(`server request error:${e}`);
    }
  }
  headers["Content-Type"] = "application/json";
  headers.responseType = "blob";
  // url = analyzeURL(url);
  fetch(API_URL + url, {
    method: "POST",
    headers: headers || {},
    body: JSON.stringify(params),
  })
    .then(async (response: any) => {
      const blob = await response.blob();

      if (["application/json", "application/json; charset=utf-8", "text/plain"].includes(blob.type)) {
        const reader = new FileReader();
        reader.readAsText(blob);
        reader.onload = () => {
          try {
            const data: any = reader.result;
            const res = JSON.parse(data);
            switch (res.code) {
              case 0:
                break;
              default:
                try {
                  showErrorMessage(res.msg);
                } catch (e) {
                  console.error(e);
                }
                break;
            }
          } catch (e) {
            console.warn(e);
          }
        };
      } else {
        //取后端设置的ContentDisposition.FileNameStar(需要设置Access-Control-Expose-Headers:Content-Disposition)
        const disposition = response.headers.get("content-disposition");
        if (!disposition) {
          try {
            showErrorMessage("服务器响应为空");
          } catch (e) {
            console.error(e);
          }

          return;
        }
        let fileName = disposition.split("filename=")[1];
        if (fileName) {
          fileName = outName || decodeURIComponent(fileName);
        }
        const link = document.createElement("a");
        link.setAttribute("download", fileName);
        link.setAttribute("href", URL.createObjectURL(blob));
        link.click();
      }
    })
    .catch((e: any) => {
      if (e.message !== "请求被取消")
        try {
          showErrorMessage(e.message);
        } catch (e) {
          console.error(e);
        }
      else console.warn(e.message);
    });
}

/**
 *  请求拦截处理异常
 */
export async function interceptor<T = any>(axiosResponse: Promise<RequestResponse>): Promise<T> {
  const data = await axiosResponse;
  if (data && data.success) {
    return data.body;
  } else {
    showErrorMessage(data?.msg || "请求失败");
  }
  return Promise.reject(data);
}

function showErrorMessage(message: string) {
  if (process.client) {
    if (DeviceUtils.isMobile()) {
      // 在 Vant 4.x 中，使用 showToast 函数
      try {
        showToast({
          message,
          duration: 5000,
        });
      } catch (e) {
        console.error("Toast error:", e);
        console.error(message);
      }
    } else {
      //如果message未出现可能是因为其他地方使用了this.$message.destroy导致message被销毁
      Message.error(message, 5);
    }
  } else {
    console.error(message);
  }
}

export type FetchEventSourceConfig = Parameters<typeof fetchEventSource>[1];
